import { queryKeys } from './queryKeys';

/**
 * The feed key for the user feed, or a group's feed when `groupId` is set.
 * `includeEvents` changes the server's pages, so it is part of the key.
 */
export function activityFeedKey({
  groupId,
  params,
  includeEvents,
}: {
  groupId?: string | null;
  params?: Record<string, unknown>;
  includeEvents: boolean;
}) {
  const keyParams = { ...params, includeEvents };
  return groupId
    ? queryKeys.groupActivityFeed(groupId, keyParams)
    : queryKeys.activityFeed(keyParams);
}

/** How many empty pages in a row a feed pulls on its own before it waits. */
export const MAX_EMPTY_PAGE_AUTO_FETCHES = 3;

/**
 * Whether a feed should pull the next page on its own: the trailing pages
 * came back empty (the server's read budget can end a page early), at most
 * `max` times in a row, and once per page count so a failed fetch is not
 * retried in a loop. The caller still checks `hasNextPage` and `isFetching`.
 */
export function shouldAutoFetchNextPage(
  pages: ReadonlyArray<{ activities: ReadonlyArray<unknown> }> | undefined,
  lastAutoFetchedAt: number | null,
  max: number
): boolean {
  if (!pages?.length || lastAutoFetchedAt === pages.length) return false;
  let trailingEmpty = 0;
  for (let i = pages.length - 1; i >= 0; i -= 1) {
    if (pages[i].activities.length > 0) break;
    trailingEmpty += 1;
  }
  return trailingEmpty > 0 && trailingEmpty <= max;
}

/** Never `includeEvents=false`: flag-off requests stay byte-identical to pre-Pro clients. */
export function appendIncludeEvents(
  params: URLSearchParams,
  includeEvents: boolean
) {
  if (includeEvents) params.append('includeEvents', 'true');
}

/**
 * A feed held back for the flags query is pending but not fetching, which
 * React Query reports as not loading; the screen would show "No activity
 * yet" instead of its skeleton. A cached page still shows.
 */
export function isFeedLoading(
  query: { isLoading: boolean; isPending: boolean },
  enabled: boolean,
  flagsReady: boolean
): boolean {
  return query.isLoading || (enabled && !flagsReady && query.isPending);
}
